/** Fixed in-memory diagnostics remain available when the primary sink fails. */
export const TELEMETRY_HEALTH_REASONS = [
  'metric_invalid_definition',
  'metric_invalid_value',
  'metric_invalid_metadata',
  'metric_tag_dropped',
  'metric_context_dropped',
  'metric_sink_missing',
  'metric_sink_failed',
  'metric_accepted',
  'log_sink_failed',
  'span_sdk_failed',
  'client_disabled',
  'client_journey_overflow',
  'client_journey_discarded',
  'client_vital_failed',
  'client_lifecycle_failed',
  'collector_buffer_overflow',
  'flush_provider_settled',
  'flush_unsupported',
  'flush_failure',
  'flush_timeout',
] as const

export type TelemetryHealthReason = (typeof TELEMETRY_HEALTH_REASONS)[number]
export type TelemetryHealthSnapshot = {
  readonly schema_version: 1
  readonly instance_id: string
  readonly sequence: number
  readonly counters: Readonly<Record<TelemetryHealthReason, number>>
}

const MAX_COUNT = Number.MAX_SAFE_INTEGER
let instances = 0

/** A separate instance is useful for isolated canaries; no global reset hides losses. */
export function createTelemetryHealthState() {
  instances += 1
  const instanceId = `health_${instances}_${Math.random().toString(36).slice(2, 12)}`
  const counters = Object.fromEntries(
    TELEMETRY_HEALTH_REASONS.map((reason) => [reason, 0])
  ) as Record<TelemetryHealthReason, number>
  let sequence = 0
  return {
    increment(reason: TelemetryHealthReason, count = 1): void {
      if (
        typeof reason !== 'string' ||
        !Object.hasOwn(counters, reason) ||
        !Number.isSafeInteger(count) ||
        count <= 0
      ) {
        return
      }
      counters[reason] = Math.min(MAX_COUNT, counters[reason] + count)
      sequence = Math.min(MAX_COUNT, sequence + 1)
    },
    snapshot(): TelemetryHealthSnapshot {
      return Object.freeze({
        schema_version: 1,
        instance_id: instanceId,
        sequence,
        counters: Object.freeze({ ...counters }),
      })
    },
  }
}

const health = createTelemetryHealthState()
export const incrementTelemetryHealth = health.increment
export const getTelemetryHealthSnapshot = health.snapshot

/** Previous snapshots belong to the same process/page instance, never a fleet total. */
export function telemetryHealthDelta(
  previous: TelemetryHealthSnapshot | undefined,
  current: TelemetryHealthSnapshot
): Readonly<Record<TelemetryHealthReason, number>> {
  const comparable = previous?.instance_id === current.instance_id
  return Object.freeze(
    Object.fromEntries(
      TELEMETRY_HEALTH_REASONS.map((reason) => [
        reason,
        Math.max(
          0,
          (Number.isSafeInteger(current.counters[reason])
            ? current.counters[reason]
            : 0) -
            (comparable && Number.isSafeInteger(previous.counters[reason])
              ? previous.counters[reason]
              : 0)
        ),
      ])
    ) as Record<TelemetryHealthReason, number>
  )
}
